import React, { useState, useEffect } from 'react';

interface ConferenceSettings {
  accepting_sponsors: boolean;
  accepting_cfp: boolean;
}

interface SettingsManagementProps {
  settings: ConferenceSettings;
  onUpdateSettings: (settings: ConferenceSettings) => Promise<void>;
}

const SettingsManagement: React.FC<SettingsManagementProps> = ({
  settings,
  onUpdateSettings,
}) => {
  const [acceptingSponsors, setAcceptingSponsors] = useState<boolean>(settings?.accepting_sponsors || false);
  const [acceptingCfp, setAcceptingCfp] = useState<boolean>(settings?.accepting_cfp || false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (settings) {
      setAcceptingSponsors(settings.accepting_sponsors);
      setAcceptingCfp(settings.accepting_cfp);
    }
  }, [settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveMessage(null);

    try {
      await onUpdateSettings({
        accepting_sponsors: acceptingSponsors,
        accepting_cfp: acceptingCfp,
      });
      setSaveMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
    } finally {
      setIsSaving(false);
      // Auto-hide the message after 3 seconds
      setTimeout(() => {
        setSaveMessage(null);
      }, 3000);
    }
  };

  if (!settings) {
    return <div className="text-center py-4">Loading settings...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Conference Settings</h2>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-800">Application Settings</h3>

            <div className="flex flex-col space-y-6">
              <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-800">Call for Speakers</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    When enabled, speakers can submit presentation proposals through the CFP system.
                  </p>
                </div>
                <div className="relative inline-block w-14 h-7 ml-4">
                  <input
                    type="checkbox"
                    id="cfp-toggle"
                    className="opacity-0 w-0 h-0"
                    checked={acceptingCfp}
                    onChange={(e) => setAcceptingCfp(e.target.checked)}
                  />
                  <label
                    htmlFor="cfp-toggle"
                    className={`absolute cursor-pointer top-0 left-0 right-0 bottom-0 rounded-full transition-colors ${
                      acceptingCfp ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`absolute h-5 w-5 rounded-full bg-white left-1 bottom-1 transition-transform ${
                        acceptingCfp ? 'transform translate-x-7' : ''
                      }`}
                    ></span>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-800">Sponsorships</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    When enabled, organizations can apply to sponsor the conference.
                  </p>
                </div>
                <div className="relative inline-block w-14 h-7 ml-4">
                  <input
                    type="checkbox"
                    id="sponsors-toggle"
                    className="opacity-0 w-0 h-0"
                    checked={acceptingSponsors}
                    onChange={(e) => setAcceptingSponsors(e.target.checked)}
                  />
                  <label
                    htmlFor="sponsors-toggle"
                    className={`absolute cursor-pointer top-0 left-0 right-0 bottom-0 rounded-full transition-colors ${
                      acceptingSponsors ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`absolute h-5 w-5 rounded-full bg-white left-1 bottom-1 transition-transform ${
                        acceptingSponsors ? 'transform translate-x-7' : ''
                      }`}
                    ></span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {saveMessage && (
            <div
              className={`p-3 rounded-md ${
                saveMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}
            >
              {saveMessage.text}
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSaving}
              className={`px-4 py-2 rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors ${
                isSaving ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isSaving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SettingsManagement;
